// src/disc/DiscReanalyze.js
import { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Autocomplete,
    Chip,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    Autorenew as AutorenewIcon,
    Person as PersonIcon,
    Work as WorkIcon,
    Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';
import api from '../config/api';

const DiscReanalyze = () => {
    const { t } = useTranslation();
    const { authToken, project, organization, showAlert, language } = useStore();

    // Estados del formulario
    const [selectedTest, setSelectedTest] = useState(null);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [currentPosition, setCurrentPosition] = useState('');
    const [selectedCompetencies, setSelectedCompetencies] = useState([]);

    // Estados de datos
    const [completedTests, setCompletedTests] = useState([]);
    const [positions, setPositions] = useState([]);
    const [competencies, setCompetencies] = useState([]);

    // Estados de carga
    const [loadingTests, setLoadingTests] = useState(false);
    const [loadingPositions, setLoadingPositions] = useState(false);
    const [loadingCompetencies, setLoadingCompetencies] = useState(false);
    const [reanalyzing, setReanalyzing] = useState(false);

    // Cargar tests completados
    useEffect(() => {
        const loadCompletedTests = async () => {
            if (!authToken || !project) return;

            try {
                setLoadingTests(true);
                const response = await api.get(
                    '/disc-tests/latest-by-project',
                    {
                        params: { projectId: project },
                        headers: { Authorization: `Bearer ${authToken}` },
                    }
                );

                if (response.data.response?.status_response === 'OK') {
                    const tests = response.data.response.data || [];
                    setCompletedTests(tests);
                }
            } catch (error) {
                console.error('Error loading completed tests:', error);
            } finally {
                setLoadingTests(false);
            }
        };

        loadCompletedTests();
    }, [authToken, project]);

    // Cargar cargos
    useEffect(() => {
        const loadPositions = async () => {
            if (!authToken || !project) return;

            try {
                setLoadingPositions(true);
                const response = await api.get(
                    '/positions',
                    {
                        params: { projectId: project },
                        headers: { Authorization: `Bearer ${authToken}` },
                    }
                );

                if (response.data.errorCode === 0 && response.data.response?.data) {
                    setPositions(response.data.response.data);
                }
            } catch (error) {
                console.error('Error loading positions:', error);
            } finally {
                setLoadingPositions(false);
            }
        };

        loadPositions();
    }, [authToken, project]);

    // Cargar competencias
    useEffect(() => {
        const loadCompetencies = async () => {
            if (!authToken) return;

            try {
                setLoadingCompetencies(true);
                const response = await api.get(
                    '/capabilities',
                    {
                        params: { language },
                        headers: { Authorization: `Bearer ${authToken}` },
                    }
                );

                if (response.data.errorCode === 0 && response.data.response?.data) {
                    setCompetencies(response.data.response.data);
                }
            } catch (error) {
                console.error('Error loading competencies:', error);
            } finally {
                setLoadingCompetencies(false);
            }
        };

        loadCompetencies();
    }, [authToken, language]);

    // Cuando se selecciona un test, limpiar los campos
    useEffect(() => {
        if (selectedTest) {
            setSelectedPosition(null);
            setCurrentPosition('');
            setSelectedCompetencies([]);
        }
    }, [selectedTest]);

    // Manejar reanálisis
    const handleReanalyze = async () => {
        if (!selectedTest) {
            showAlert(t('disc.pleaseSelectApplicant'), 'warning');
            return;
        }

        // Validar que haya al menos cargo o competencias
        if (!selectedPosition && selectedCompetencies.length === 0) {
            showAlert(t('disc.reanalyzeValidation'), 'warning');
            return;
        }

        setReanalyzing(true);

        try {
            const payload = {};

            // Enviar lo mismo que createDiscTest
            if (selectedPosition) {
                payload.positionId = selectedPosition.uid;
                payload.positionName = selectedPosition.name;
                if (selectedPosition.description) {
                    payload.positionDescription = selectedPosition.description;
                }
            }

            if (selectedCompetencies.length > 0) {
                payload.competencies = selectedCompetencies.map(c => ({
                    competencyId: c.id,
                    competencyName: c.name
                }));
            }

            const response = await api.post(
                `/disc-tests/${selectedTest.id}/reanalyze`,
                payload,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );

            if (response.data.response?.status_response === 'OK') {
                const newTestId = response.data.response.data?.newTestId;
                showAlert(
                    t('disc.reanalyzeSuccess', { testId: newTestId }),
                    'success'
                );

                // Limpiar formulario
                setSelectedTest(null);
                setSelectedPosition(null);
                setCurrentPosition('');
                setSelectedCompetencies([]);
            } else {
                showAlert(t('disc.testSentError'), 'error');
            }
        } catch (error) {
            console.error('Error al reanalizar:', error);
            showAlert(
                error.response?.data?.response?.message || t('disc.testSentError'),
                'error'
            );
        } finally {
            setReanalyzing(false);
        }
    };

    // Verificar si el botón debe estar deshabilitado
    const isReanalyzeDisabled = !selectedTest || reanalyzing;

    return (
        <Box>
            {/* Header */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    {t('disc.reanalyze')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('disc.reanalyzeDescription')}
                </Typography>
            </Paper>

            {/* Alert informativo */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <strong>{t('disc.reanalyzeNote')}</strong> {t('disc.reanalyzeNoteText')}
            </Alert>

            {/* Aviso sobre tests evaluados */}
            {completedTests.length > 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        {t('disc.evaluatedTestsNotice')}
                    </Typography>
                </Alert>
            )}

            {/* Formulario */}
            <Paper sx={{ p: 3 }}>
                {/* Selector de postulante con test completado */}
                <Box sx={{ mb: 3 }}>
                    <Autocomplete
                        options={completedTests}
                        getOptionLabel={(option) => option.applicantName || ''}
                        value={selectedTest}
                        onChange={(event, newValue) => setSelectedTest(newValue)}
                        loading={loadingTests}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('disc.selectApplicant')}
                                placeholder={t('disc.selectApplicantPlaceholder')}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />,
                                    endAdornment: (
                                        <>
                                            {loadingTests ? <CircularProgress size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props}>
                                <Box>
                                    <Typography variant="body1">{option.applicantName}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.applicantEmail}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                    />
                </Box>

                {/* Campos adicionales - Solo se muestran si hay test seleccionado */}
                {selectedTest && (
                    <>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            <strong>{t('disc.reanalyzeWarning')}</strong> {t('disc.reanalyzeWarningText')}
                        </Alert>

                        {/* Selector de cargo */}
                        <Box sx={{ mb: 3 }}>
                            <Autocomplete
                                options={positions}
                                getOptionLabel={(option) => option.name || ''}
                                value={selectedPosition}
                                onChange={(event, newValue) => setSelectedPosition(newValue)}
                                loading={loadingPositions}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`${t('disc.selectPosition')} ${t('disc.optional')}`}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: <WorkIcon sx={{ mr: 1, color: 'action.active' }} />,
                                            endAdornment: (
                                                <>
                                                    {loadingPositions ? <CircularProgress size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        {/* Campo de puesto actual */}
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                label={`${t('disc.currentPosition')} ${t('disc.optional')}`}
                                placeholder={t('disc.currentPositionPlaceholder')}
                                value={currentPosition}
                                onChange={(e) => setCurrentPosition(e.target.value)}
                            />
                        </Box>

                        {/* Selector de competencias */}
                        <Box sx={{ mb: 3 }}>
                            <Autocomplete
                                multiple
                                options={competencies}
                                getOptionLabel={(option) => option.name || ''}
                                value={selectedCompetencies}
                                onChange={(event, newValue) => setSelectedCompetencies(newValue)}
                                loading={loadingCompetencies}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            key={option.id}
                                            label={option.name}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`${t('disc.selectCompetencies')} ${t('disc.optional')}`}
                                        placeholder={t('disc.competenciesDescription')}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <PsychologyIcon sx={{ mr: 1, color: 'action.active' }} />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {loadingCompetencies ? <CircularProgress size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        {/* Botón de reanalizar */}
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            startIcon={reanalyzing ? <CircularProgress size={20} color="inherit" /> : <AutorenewIcon />}
                            onClick={handleReanalyze}
                            disabled={isReanalyzeDisabled}
                        >
                            {reanalyzing ? t('disc.reanalyzing') : t('disc.reanalyzeButton')}
                        </Button>
                    </>
                )}

                {/* Mensaje si no hay test seleccionado */}
                {!selectedTest && completedTests.length > 0 && (
                    <Alert severity="info">
                        {t('disc.selectApplicantToStart')}
                    </Alert>
                )}

                {/* Mensaje si no hay tests completados */}
                {completedTests.length === 0 && !loadingTests && (
                    <Alert severity="warning">
                        {t('disc.noCompletedTestsForReanalysis')}
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default DiscReanalyze;